import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Spinner from 'components/Common/Spinner';
import * as urls from 'routes/config';

import Team from 'pages/Agency/Team';
import TeamForm from 'pages/Agency/Team/components/TeamMemberForm';
import Configuration from 'pages/Agency/Configuration';
import Billing from 'pages/Agency/Billing';
import Integrations from 'pages/Agency/Integrations';
import Integration from 'pages/Agency/Integrations/components/Integration';
import ConfigureIntegration from 'pages/Agency/Integrations/components/Configure';
import Smtp from 'pages/Agency/Configuration/Smtp';
import BusinessProfileForm from 'pages/Business/Setting/BusinessProfile';
import Businesses from 'pages/Agency/Businesses';
import Pricing from 'pages/Agency/Configuration/pricing';
import BrandingAndDomain from 'pages/Agency/Configuration/branding';
import ThirdParty from 'pages/Agency/Configuration/ThirdParty';
import Revenue from 'pages/Agency/Revenue';
import AuditTrails from 'pages/Agency/AuditTrails';
import PlanForm from '@/pages/Agency/Configuration/pricing/components/PackagesForm';
import Profile from '@/pages/UserProfile/index';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import Coupons from '@/pages/Agency/Coupon';
import CouponForm from '@/pages/Agency/Coupon/components/CouponForm';
import Developers from '@/pages/Agency/Developers';
import ApiKeys from '@/pages/Agency/Developers/ApiKeys';
import Webhooks from '@/pages/Agency/Webhooks';
import WebhookForm from '@/pages/Agency/Webhooks/components/WebhookForm';
import WebhookLogDetail from '@/pages/Agency/Webhooks/components/WebhookLogDetail';
import VoiceEngines from '@/pages/Agency/VoiceEngines';
import OnboardingForms from '@/pages/Agency/OnboardingForms';
import AgentReadinessForm from '@/pages/Agency/OnboardingForms/AgentReadinessForm';
import HistoryLogsDrawer from '@/pages/Agency/OnboardingForms/components/HistoryLogsDrawer';
import AgencyCallLogs from '@/pages/Agency/CallLogs';
import AgencyChatLogs from '@/pages/Agency/ChatLogs';
import AgencySyncLogs from '@/pages/Agency/SyncLogs';
import AgencySmsLogs from '@/pages/Agency/SmsLogs';
import AgencyRecommendations from '@/pages/Agency/Recommendations';
import IntraofficeNotes from '@/pages/Agency/IntraofficeNotes';
import IntraofficeNotesForm from '@/pages/Agency/IntraofficeNotes/components/IntraofficeNotesForm';
import SystemSentiment from '@/pages/Agency/SystemSentiment';
import CallNeedingAuditForm from '@/pages/Agency/SystemSentiment/components/CallNeedingAuditForm';
import Telephony from '@/pages/Agency/Telephony';

export default function AgencyRoutes() {
  const { data: agencyOrganizationData, isLoading: isAgencyLoading } = useGetAgencyOrganizations();

  if (isAgencyLoading) {
    return null;
  }
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route index element={<Businesses />} />
        <Route path='businesses/add' element={<BusinessProfileForm agencyView={true} />} />
        <Route path={urls.agency.onboardingForms} element={<OnboardingForms />}>
          <Route path='history/:onboardingFormId' element={<HistoryLogsDrawer />} />
        </Route>
        <Route path={urls.agency.onboardingFormEdit()} element={<AgentReadinessForm isEditingView={true} />} />
        {Boolean(agencyOrganizationData?.organization_plan_id) && (
          <Route path={urls.agency.billing} element={<Billing />} />
        )}
        <Route path={urls.agency.systemSentiment} element={<SystemSentiment />}>
          <Route path=':callNeedingAuditId' element={<CallNeedingAuditForm />} />
        </Route>
        <Route path={urls.agency.revenue} element={<Revenue />} />
        <Route path={urls.agency.auditTrails} element={<AuditTrails />} />
        <Route path={urls.agency.callLogs} element={<AgencyCallLogs />}>
          <Route path=':callNeedingAuditId' element={<CallNeedingAuditForm view='call-logs' />} />
        </Route>
        <Route path={urls.agency.chatLogs} element={<AgencyChatLogs />}>
          <Route path=':callNeedingAuditId' element={<CallNeedingAuditForm view='chat-logs' />} />
        </Route>
        <Route path={urls.agency.syncLogs} element={<AgencySyncLogs />} />
        <Route path={urls.agency.smsLogs} element={<AgencySmsLogs />} />
        <Route path={urls.agency.recommendations} element={<AgencyRecommendations />} />
        <Route path={urls.agency.businessProfileEdit()} element={<BusinessProfileForm agencyView={true} />} />
        <Route path={urls.agency.team} element={<Team />}>
          <Route path='add' element={<TeamForm />} />
          <Route path=':teamId' element={<TeamForm />} />
        </Route>
        <Route path={urls.agency.intraofficeNotes} element={<IntraofficeNotes />}>
          <Route path='add' element={<IntraofficeNotesForm />} />
          <Route path=':noteId' element={<IntraofficeNotesForm />} />
        </Route>
        <Route path={urls.agency.configuration} element={<Configuration />}>
          <Route index element={<Navigate to={urls.configuration.branding} />} />
          <Route path={urls.configuration.smtp} element={<Smtp />} />
          {/* Location Routes */}
          <Route path={urls.configuration.pricing} element={<Pricing />}>
            <Route path='add' element={<PlanForm />} />
            <Route path=':packageId' element={<PlanForm />} />
          </Route>

          <Route path={urls.configuration.branding} element={<BrandingAndDomain />} />
          <Route path={urls.configuration.thirdParty} element={<ThirdParty />} />
        </Route>
        <Route path={`${urls.agency.telephony}/:provider/:tab`} element={<Telephony />} />
        <Route
          path={urls.agency.telephony}
          element={<Navigate to={`/agency/${urls.agency.telephony}/twilio/api-key`} replace />}
        />
        <Route path={urls.agency.voiceEngines} element={<VoiceEngines />} />
        <Route path={urls.agency.integrations} element={<Integrations />} />
        <Route path={urls.agency.coupons} element={<Coupons />}>
          <Route path='add' element={<CouponForm />} />
          <Route path=':couponId' element={<CouponForm />} />
        </Route>
        <Route path={urls.agency.developers} element={<Developers />}>
          <Route index element={<Navigate to={urls.developers.webhooks} replace />} />
          <Route path={urls.developers.apiKeys} element={<ApiKeys />} />
          <Route path={urls.developers.webhooks} element={<Webhooks />}>
            <Route path='add' element={<WebhookForm />} />
            <Route path='logs/:logId' element={<WebhookLogDetail />} />
            <Route path=':webhookId' element={<WebhookForm />} />
          </Route>
        </Route>
        {/* Legacy webhook URL */}
        <Route
          path='webhooks/*'
          element={<Navigate to={`/agency/${urls.agency.developers}/${urls.developers.webhooks}`} replace />}
        />

        <Route path={urls.agency.integration} element={<Integration />}>
          <Route path={urls.agency.configureIntegration} element={<ConfigureIntegration />} />
        </Route>
        <Route path={urls.agency.profile} element={<Profile />} />
        <Route path='*' element={<Navigate to='/404' replace />} />
      </Routes>
    </Suspense>
  );
}
